import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Headset, Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiSupportStaffDelete, useSupportStaffList } from '@/entities/support-staff';
import { formatDate } from '@/shared/utils/date';
import type { ApiSupportStaffGetItem } from '@/shared/types/api/support-staff';

const PAGE_SIZE = 20;

export const WidgetsSupportStaffMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const canCreate = usePerm('support_staff.create');
  const canEdit = usePerm('support_staff.edit');
  const canDelete = usePerm('support_staff.delete');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ApiSupportStaffGetItem | null>(null);

  const params = useMemo(() => ({}), []);
  const query = useSupportStaffList(params);
  const all = query.data ?? [];
  const total = all.length;
  const pageStart = (page - 1) * PAGE_SIZE;
  const rows = all.slice(pageStart, pageStart + PAGE_SIZE);

  const remove = useMutate({
    api: (id: string) => apiSupportStaffDelete(id),
    invalidateKeys: [queryKeys.supportStaff.all],
    onSuccess: () => {
      toast.info(t('supportStaff.toast.deleted'));
      setDeleteTarget(null);
    },
    onError: (_e, m) => toast.error(m),
  });

  const columns = useMemo<ColumnDef<ApiSupportStaffGetItem>[]>(
    () => [
      {
        accessorKey: 'image',
        header: t('supportStaff.list.columns.image'),
        enableSorting: false,
        cell: (info) => {
          const { image, name } = info.row.original;
          return image ? (
            <img
              src={image}
              alt={name}
              className="h-10 w-10 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-ink-faint">
              <UserRound className="h-5 w-5" aria-hidden />
            </span>
          );
        },
      },
      {
        accessorKey: 'name',
        header: t('supportStaff.list.columns.name'),
        cell: (info) => <span className="font-medium text-ink">{info.row.original.name}</span>,
      },
      {
        accessorKey: 'contact_link',
        header: t('supportStaff.list.columns.contact'),
        enableSorting: false,
        cell: (info) => {
          const link = info.row.original.contact_link;
          return (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="block max-w-[220px] truncate text-sm font-medium text-brand-600 hover:underline"
            >
              {link}
            </a>
          );
        },
      },
      {
        accessorKey: 'languages',
        header: t('supportStaff.list.columns.languages'),
        enableSorting: false,
        cell: (info) => {
          const langs = info.row.original.languages;
          if (langs.length === 0) return <span className="text-ink-muted">—</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {langs.map((lang) => (
                <span
                  key={lang}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700 whitespace-nowrap"
                >
                  {lang}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'experience_years',
        header: t('supportStaff.list.columns.experience'),
        cell: (info) => {
          const years = info.row.original.experience_years;
          if (years === null) return <span className="text-ink-muted">—</span>;
          return t('supportStaff.list.yearsSuffix', { count: years });
        },
      },
      {
        accessorKey: 'created_at',
        header: t('supportStaff.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('supportStaff.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <IconButton
                  label={t('common.edit')}
                  onClick={() => navigate(`/support-staff/${row.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  label={t('common.delete')}
                  variant="danger"
                  onClick={() => setDeleteTarget(row)}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [t, canEdit, canDelete, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('supportStaff.list.title')}
        subtitle={t('supportStaff.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/support-staff/create')}
              title={t('supportStaff.list.create')}
              aria-label={t('supportStaff.list.create')}
            >
              <span className="hidden sm:inline">{t('supportStaff.list.create')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('supportStaff.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('supportStaff.list.emptyTitle')}
              emptyIcon={<Headset className="h-8 w-8" />}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t('supportStaff.confirmDelete.title')}
        description={t('supportStaff.confirmDelete.description', {
          name: deleteTarget?.name ?? '',
        })}
        confirmLabel={t('supportStaff.confirmDelete.confirm')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
      />
    </>
  );
};
